/**
 * Filters for the Submission Pack list (Specs/2026-09-23-submission-pack-redesign-plan.md,
 * Task 3).
 *
 * Pure so the page, the filter bar and the tests share one definition of
 * "matches". State lives in `SubmissionPackPage`; `PackList` receives the
 * already-filtered items.
 */

import type { PackItem, TriageClass } from '@/lib/api'

/**
 * The segmented control. Each value maps to exactly one EFFECTIVE readiness;
 * `not_required` rows have no segment and show only under "all".
 */
export type PackStatusFilter = 'all' | 'todo' | 'in_progress' | 'ready'

export type PackTypeFilter = 'all' | TriageClass

export interface PackFilters {
  search: string
  status: PackStatusFilter
  /** Only items owned by the current user. */
  mine: boolean
  type: PackTypeFilter
  /**
   * Hidden mode set by the "over the word limit" blocker chip. It has no
   * control of its own — the scope line names it and Clear resets it.
   */
  overLimit: boolean
}

export const DEFAULT_PACK_FILTERS: PackFilters = {
  search: '',
  status: 'all',
  mine: false,
  type: 'all',
  overLimit: false,
}

/** True when anything narrows the list. */
export function isPackFilterActive(filters: PackFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.status !== 'all' ||
    filters.mine ||
    filters.type !== 'all' ||
    filters.overLimit
  )
}

/** A null triage class counts as an attachment — the same fallback the grouping uses. */
function triageOf(item: PackItem): TriageClass {
  return item.triageClass ?? 'attachment'
}

const STATUS_READINESS: Record<Exclude<PackStatusFilter, 'all'>, PackItem['readiness']> = {
  todo: 'not_ready',
  in_progress: 'in_progress',
  ready: 'ready',
}

/**
 * Over-limit is read off `derived`, not `readiness` — a row a human marked
 * ready is still over the limit. `not_required` is excluded to match the
 * server's `overWordLimit` blocker count, which the chip shows.
 */
export function isOverWordLimit(item: PackItem): boolean {
  return item.readiness !== 'not_required' && item.derived.overWordLimit
}

function matchesSearch(item: PackItem, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true
  return [item.title, item.description, item.method, item.format].some(
    (field) => field != null && field.toLowerCase().includes(needle)
  )
}

export function matchesPackFilters(
  item: PackItem,
  filters: PackFilters,
  currentUserId: string | null
): boolean {
  if (filters.status !== 'all' && item.readiness !== STATUS_READINESS[filters.status]) {
    return false
  }
  if (filters.overLimit && !isOverWordLimit(item)) return false
  if (filters.mine && (currentUserId === null || item.ownerId !== currentUserId)) return false
  if (filters.type !== 'all' && triageOf(item) !== filters.type) return false
  return matchesSearch(item, filters.search)
}

export function filterPackItems(
  items: PackItem[],
  filters: PackFilters,
  currentUserId: string | null
): PackItem[] {
  if (!isPackFilterActive(filters)) return items
  return items.filter((item) => matchesPackFilters(item, filters, currentUserId))
}

/** Folding done rows only makes sense when the list is not narrowed by status. */
export function shouldFoldDone(filters: PackFilters): boolean {
  return filters.status === 'all' && !filters.overLimit
}
